import type { InputProps } from '@ab-core/input';
import { isRequired } from './validationFilter';

export type ValidationChecksType = (field: FormObjectContent) => boolean;

export type FormObjectContent = {
    inputProps: InputProps;
    validationChecks?: Array<ValidationChecksType>;
};

export type FormObject = {
    fields: Record<string, FormObjectContent>;
    formIsValid: boolean;
};

export type FormObjectContentWithKey = FormObjectContent['inputProps'] & { key: string };

export enum ACTIONS {
    SET_FIELD = 'setField',
    SET_FIELD_PROPS = 'setFieldProps',
    CHECK_VALIDATION = 'checkValidation',
    SET_MULTIPLE_FIELDS = 'setMultipleFields',
    SET_FIELD_VALIDATION = 'setFieldValidation'
}

export type Payload = {
    key: string;
    value: string;
};

export type PropsWithKey = { key: string; props: InputProps };

type ValidationWithKey = { key: string; validationChecks: Array<ValidationChecksType> };

type ActionTypes =
    | { type: ACTIONS.CHECK_VALIDATION }
    | { type: ACTIONS.SET_FIELD; payload: Payload }
    | { type: ACTIONS.SET_FIELD_PROPS; payload: PropsWithKey }
    | { type: ACTIONS.SET_MULTIPLE_FIELDS; payload: { fields: Array<PropsWithKey> } }
    | { type: ACTIONS.SET_FIELD_VALIDATION; payload: ValidationWithKey };

export type ActionType = ActionTypes;

const checkFieldsValidation = (fields: Record<string, FormObjectContent>): boolean => {
    let fieldsAreValid = true;

    for (const key in fields) {
        const field = fields[key];
        const checkValidation = checkFieldValidation(field);

        if (fieldsAreValid) {
            fieldsAreValid = checkValidation;
        }
    }

    return fieldsAreValid;
};

const checkFieldValidation = (field: FormObjectContent): boolean => {
    let fieldIsValid = true;
    const validationChecks = [...(field.validationChecks || [])];

    if (field.inputProps.required) {
        validationChecks.push(isRequired);
    }

    validationChecks?.forEach((validationCheck) => {
        const checkValidation = validationCheck(field);

        if (fieldIsValid) {
            fieldIsValid = checkValidation;
        }
    });

    if (fieldIsValid) {
        field.inputProps.errorMessage = undefined;
    }

    return fieldIsValid;
};

export function reducer(state: FormObject, action: ActionType): FormObject {
    switch (action.type) {
        case ACTIONS.SET_FIELD: {
            const { key } = action.payload;
            const newField = state.fields[key];
            newField.inputProps.value = action.payload.value;
            checkFieldValidation(newField);

            return {
                ...state,
                fields: {
                    ...state.fields,
                    [key]: newField
                }
            };
        }

        case ACTIONS.SET_FIELD_PROPS: {
            const { key } = action.payload;
            const newField = state.fields[key];
            newField.inputProps = { ...newField.inputProps, ...action.payload.props };
            checkFieldValidation(newField);

            return {
                ...state,
                fields: {
                    ...state.fields,
                    [key]: newField
                }
            };
        }

        case ACTIONS.SET_FIELD_VALIDATION: {
            const { key } = action.payload;
            const newField = state.fields[key];
            newField.validationChecks = action.payload.validationChecks;
            checkFieldValidation(newField);

            return {
                ...state,
                fields: {
                    ...state.fields,
                    [key]: newField
                }
            };
        }

        case ACTIONS.SET_MULTIPLE_FIELDS: {
            const { fields } = action.payload;
            const newFields: FormObject['fields'] = {};
            fields.forEach((field) => {
                const newField = state.fields[field.key];
                newField.inputProps = { ...newField.inputProps, ...field.props };
                newFields[field.key] = newField;
                checkFieldValidation(newField);
            });

            return {
                ...state,
                fields: { ...state.fields, ...newFields }
            };
        }

        case ACTIONS.CHECK_VALIDATION: {
            const { fields } = state;
            const newFields = fields;
            const formIsValid = checkFieldsValidation(newFields);

            return { ...state, formIsValid, fields: { ...state.fields, ...newFields } };
        }

        default: {
            return state;
        }
    }
}
